import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { clearAuthHistory, isApp, isIOS } from '../utils/platform'
import { loadNaverMapScript, isNaverMapLoaded } from '../utils/naverMapLoader'
import SearchBar from '../components/SearchBar'
import CategoryFilter from '../components/CategoryFilter'
import BottomNav from '../components/BottomNav'
import StoreBottomSheet from '../components/StoreBottomSheet'
import PreviewPlaceBottomSheet from '../components/PreviewPlaceBottomSheet'
import PlacePickBottomSheet from '../components/PlacePickBottomSheet'
import PlaylistFilterSheet, {
  prefetchPlaylistFilter,
} from '../components/PlaylistFilterSheet'
import LoadingSpinner from '../components/LoadingSpinner'
import { MapMarker } from '../types/mapMarker'
import './MainMap.scss'
import {
  BOTTOM_NAV_HEIGHT,
  MARKER_HEIGHT,
  MARKER_WIDTH,
  MAP_SEARCH_BAR_HEIGHT,
  CLUSTER_MARKER_SIZE,
} from '../constants/layout'
import { MapFilterType, MAP_FILTERS } from '../constants/filter'
import { RANKING_NAV_ITEM } from '../types/navItem'
import type { Category } from '../types/category'
import { mapApi } from '../api/map'
import { kakaoApi, KakaoPlace } from '../api/kakao'
import { useCategoryStore } from '../stores/categoryIconStore'
import { useLocationStore } from '../stores/locationStore'
import { useAuthStore } from '../stores/authStore'
import { toastUtil } from '../utils/toast'
import { guideLocationPermission } from '../utils/locationPermission'
import { userApi } from '../api/user'
import { thumbUrl } from '../utils/photoThumb'

const mapContainerStyle = {
  width: '100%',
  height: '100%',
}

const defaultCenter = {
  lat: 37.5665,
  lng: 126.978,
}

const defaultZoom = 15
const FOCUS_ZOOM = 17 // 검색/포커스 장소 진입 시 조금 더 확대
const MARKER_BADGE_SIZE = 40 // 경험/저장 장소 원형 마커(별·북마크) 크기(px)

/**
 * 지도를 탭했을 때 "이 자리"로 인정하는 반경(m).
 *
 * 손가락 접촉면이 20~30px이고 줌 16에서 1px이 약 2.4m라, 탭 지점 자체가 이미
 * 수십 m의 불확실성을 갖는다. 이 반경 안에 여러 곳이 잡히는 건 정상이며,
 * 그때는 좌표로 고르지 않고 사용자에게 묻는다.
 */
const TAP_RADIUS = 15
const MIN_ZOOM = 7
const MAX_ZOOM = 21
const HIGH_DENSITY_CLUSTER_MAX_ZOOM = 13
const DEFAULT_CLUSTER_DISSOLVE_ZOOM = 15

// 이 줌 미만이면 접시 대신 작은 점(dot)으로 표시 (클러스터링 대체)
const DOT_ZOOM = 15
const DOT_SIZE = 16 // 점 지름(px)

const MainMap: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  // 진입 시 기본 필터 '경험한 장소' 자동 선택 → 마커 자동 로드 + 이동 시 재검색 버튼 노출
  // 처음엔 맨 앞 칩인 '기록된 장소'를 고른다. 이 필터를 못 쓰는 계정이면
  // 프로필을 받은 뒤 '경험한 장소'로 물린다(아래 canUseRecordedFilter).
  const [selectedFilter, setSelectedFilter] = useState<MapFilterType | null>(
    MapFilterType.RECORDED
  )
  // PLAYLIST 필터: 선택한 플레이트리스트 id + 선택 시트 열림 상태
  const [selectedPlaylistId, setSelectedPlaylistId] = useState<number | null>(
    null
  )
  const [isPlaylistSheetOpen, setIsPlaylistSheetOpen] = useState(false)
  /**
   * 고를 플레이트리스트가 있는지 — 내가 만든 것 + 초대받은 것. 기본 '저장됨'은 '저장한 장소'라 안 센다.
   * 처음엔 모름(null)이라 칩을 숨겨 둔다. 맨 끝 칩이라 늦게 붙어도 다른 칩이 밀리지 않는다.
   */
  const [hasPlaylists, setHasPlaylists] = useState<boolean | null>(null)
  const [map, setMap] = useState<naver.maps.Map | null>(null)
  const [zoom, setZoom] = useState<number>(defaultZoom)
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null)
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false)
  const [isPreviewBottomSheetOpen, setIsPreviewBottomSheetOpen] =
    useState(false)
  const [markers, setMarkers] = useState<MapMarker[]>([])
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const [mapLoadError, setMapLoadError] = useState<string | null>(null)
  const [isLoadingMarkers, setIsLoadingMarkers] = useState(false)
  const [shouldShowReloadButton, setShouldShowReloadButton] = useState(false)
  const [mapCenter, setMapCenter] = useState<{
    lat: number
    lng: number
  } | null>(null)
  const [isMapInitialized, setIsMapInitialized] = useState(false)
  // placeId·좌표로 진입 시, 기본중심(서울시청)이 잠깐 보였다가 장소로 점프하는 깜빡임 방지용.
  // 카메라가 대상 장소로 이동 완료될 때까지 로딩 오버레이를 유지한다.
  const [isFocusPending, setIsFocusPending] = useState(false)
  const [placeToFocus, setPlaceToFocus] = useState<MapMarker | null>(null)
  /** 쿼리스트링 moveLat/moveLng로 진입 — 마커 없이 카메라만 이동 */
  const [centerToMove, setCenterToMove] = useState<{
    lat: number
    lng: number
  } | null>(null)
  /** 좌표 쿼리(placeLat 등)로만 넘어온 위치 — API 마커 로드와 별도로 핀 유지 */
  const [previewPinMarker, setPreviewPinMarker] = useState<MapMarker | null>(
    null
  )
  /** placeId 딥링크로 포커스한 DB 장소 — fetchMarkers가 markers를 덮어써도 핀 유지 */
  const [focusedPlacePinMarker, setFocusedPlacePinMarker] =
    useState<MapMarker | null>(null)
  const [clusterDissolveZoom, setClusterDissolveZoom] = useState<number>(
    DEFAULT_CLUSTER_DISSOLVE_ZOOM
  )
  const loadCategories = useCategoryStore((state) => state.loadCategories)
  const userLocation = useLocationStore((state) => state.location)
  const getUserLocation = useLocationStore((state) => state.getUserLocation)
  const requestLocationPermission = useLocationStore(
    (state) => state.requestLocationPermission
  )
  const getMarkerPin = useCategoryStore((state) => state.getMarkerPin)
  // 경험한 장소 마커(접시 프레임) 안에 들어갈 내 프로필 사진
  const myProfileImage = useAuthStore((state) => state.profileImage)
  // '기록된 장소' 필터는 서버가 허용한 계정에만 보인다 (2026-11-01 기준 전원 허용)
  const [canUseRecordedFilter, setCanUseRecordedFilter] = useState(false)
  /** 못 쓰는 계정이라 기본 선택을 '경험한 장소'로 물렸다 — 마커도 다시 받아야 한다 */
  const recordedFallbackRef = useRef(false)
  useEffect(() => {
    let alive = true
    userApi
      .getProfile()
      .then((res) => {
        if (!alive) return
        const allowed = !!res.user.can_use_recorded_filter
        setCanUseRecordedFilter(allowed)
        // 칩이 안 보이는데 선택돼 있으면 안 된다 — 예전 기본값으로 물린다
        if (!allowed && selectedFilterRef.current === MapFilterType.RECORDED) {
          recordedFallbackRef.current = true
          setSelectedFilter(MapFilterType.EXPERIENCED)
        }
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [])
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const userLocationMarkerRef = useRef<naver.maps.Marker | null>(null)
  const placeMarkersRef = useRef<naver.maps.Marker[]>([])
  // 빈 좌표 클릭 시 그 위치에 표시하는 마커 (바텀시트 닫으면 제거)
  const clickMarkerRef = useRef<naver.maps.Marker | null>(null)

  /** 탭한 자리에 가게가 여러 곳일 때의 후보들 (한 곳이면 바로 선택되고 비어 있다) */
  const [tapCandidates, setTapCandidates] = useState<KakaoPlace[]>([])

  /**
   * 지도 클릭 리스너는 지도 초기화 때 한 번만 등록되어 그 시점의 값에 묶인다.
   * 최신 선택 함수를 ref로 건네 오래된 상태를 참조하지 않게 한다.
   */
  const selectTappedPlaceRef = useRef<
    ((place: KakaoPlace) => Promise<void>) | null
  >(null)
  // 위치 핀(검색/클릭)이 떠 있는지 — 있으면 접시·점 전부 숨기고 핀만 표시(네이버 동일)
  const [hasLocationMarker, setHasLocationMarker] = useState(false)
  const markerClickListenersRef = useRef<any[]>([])
  const markerClusteringRef = useRef<any>(null)
  const selectedFilterRef = useRef<MapFilterType | null>(selectedFilter)
  const selectedPlaylistIdRef = useRef<number | null>(selectedPlaylistId)
  const isLoadingMarkersRef = useRef<boolean>(false)
  const lastLoadedBoundsRef = useRef<{
    north: number
    south: number
    east: number
    west: number
  } | null>(null)
  const hasNavigatedFromPlaceIdRef = useRef(false)

  // ref 업데이트는 직접 할당
  selectedFilterRef.current = selectedFilter
  selectedPlaylistIdRef.current = selectedPlaylistId
  isLoadingMarkersRef.current = isLoadingMarkers

  // 컨테이너에 크기가 생길 때까지 대기 (iOS WebView 레이아웃 타이밍 이슈 대응)
  const waitForContainerSize = (
    container: HTMLDivElement,
    maxWaitMs = 2000
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const check = () => {
        const rect = container.getBoundingClientRect()
        if (rect.width > 0 && rect.height > 0) {
          resolve()
          return
        }
        if (maxWaitMs <= 0) {
          reject(new Error('지도 영역 크기를 가져올 수 없습니다.'))
          return
        }
        maxWaitMs -= 50
        setTimeout(check, 50)
      }
      check()
    })
  }

  // 네이버 맵 초기화 함수
  const initMap = async (initialCenter?: { lat: number; lng: number }) => {
    console.log('[MainMap] initMap 시작')
    if (!mapContainerRef.current) {
      console.log('[MainMap] mapContainerRef 없음, return')
      return
    }

    try {
      const rect = mapContainerRef.current.getBoundingClientRect()
      console.log('[MainMap] 컨테이너 크기 대기 전', {
        width: rect.width,
        height: rect.height,
      })
      await waitForContainerSize(mapContainerRef.current)
      console.log('[MainMap] 컨테이너 크기 준비됨')

      await loadNaverMapScript()
      console.log('[MainMap] loadNaverMapScript 완료')

      const isLoaded = isNaverMapLoaded()
      const naverMaps = (window as any).naver?.maps
      const hasMap = !!naverMaps?.Map
      console.log('[MainMap] naver.maps 상태', {
        isLoaded,
        hasNaverMaps: !!naverMaps,
        hasMapConstructor: hasMap,
        naverKeys: naverMaps ? Object.keys(naverMaps).slice(0, 10) : [],
      })

      if (!isLoaded) {
        throw new Error('네이버 맵 API를 로드할 수 없습니다.')
      }

      if (!naverMaps?.Map) {
        throw new Error(
          '네이버 맵 API가 아직 준비되지 않았습니다. 잠시 후 다시 시도해주세요.'
        )
      }

      // 초기 중심 위치 결정: 위치 정보가 있으면 그 위치, 없으면 기본 위치
      const center = initialCenter || defaultCenter

      // 지도 생성
      const mapInstance = new naverMaps.Map(mapContainerRef.current, {
        center: new naverMaps.LatLng(center.lat, center.lng),
        zoom: defaultZoom,
        minZoom: MIN_ZOOM,
        maxZoom: MAX_ZOOM,
        zoomControl: false,
        scaleControl: false,
        logoControl: false,
        mapDataControl: false,
        // 확대는 핀치 줌으로만 — 더블탭(터치)/더블클릭(마우스) 확대 비활성
        disableDoubleTapZoom: true,
        disableDoubleClickZoom: true,
      })

      console.log('[MainMap] 지도 생성 완료')
      setMap(mapInstance)
      setZoom(defaultZoom)
      console.log('[MainMap] zoom (initial)', mapInstance.getZoom())
      setIsMapLoaded(true)
      setMapLoadError(null)

      // 맵 초기화 완료 후 초기 마커 가져오기 (지연 시간 최소화)
      // 필터가 선택되지 않았으면 마커를 불러오지 않음
      setTimeout(async () => {
        if (!selectedFilterRef.current) {
          setMarkers([])
          return
        }
        if (!isLoadingMarkersRef.current) {
          setIsLoadingMarkers(true)
          isLoadingMarkersRef.current = true
          try {
            const bounds = mapInstance.getBounds()
            if (!bounds) {
              setIsLoadingMarkers(false)
              isLoadingMarkersRef.current = false
              return
            }

            const sw = bounds.getSW ? bounds.getSW() : bounds.getSouthWest()
            const ne = bounds.getNE ? bounds.getNE() : bounds.getNorthEast()

            if (!sw || !ne) {
              setIsLoadingMarkers(false)
              isLoadingMarkersRef.current = false
              return
            }

            const boundsData = {
              north: ne.lat(),
              south: sw.lat(),
              east: ne.lng(),
              west: sw.lng(),
            }

            const response = await mapApi.getMarkers({
              bounds: boundsData,
              filter: selectedFilterRef.current,
              bookmarkCategoryId:
                selectedFilterRef.current === MapFilterType.PLAYLIST
                  ? selectedPlaylistIdRef.current
                  : undefined,
            })

            setMarkers(response.markers)
            lastLoadedBoundsRef.current = boundsData
            setShouldShowReloadButton(false)
          } catch (error: any) {
            console.error('마커 데이터 가져오기 실패:', error)
            if (error?.response?.data) {
              console.error('에러 상세 정보:', {
                message: error.response.data.message,
                error: error.response.data.error,
                errors: error.response.data.errors,
              })
            }
            setMarkers([])
          } finally {
            setIsLoadingMarkers(false)
            isLoadingMarkersRef.current = false
          }
        }
      }, 100)

      // 네이버 맵 로고 및 스케일 컨트롤 숨기기
      const hideNaverMapControls = () => {
        if (!mapContainerRef.current) return

        // 맵 컨테이너 내부의 모든 요소 검색
        const mapContainer = mapContainerRef.current

        // 네이버 로고 링크 숨기기
        const logoLinks = mapContainer.querySelectorAll(
          'a[href*="naver.net"], a[href*="pstatic.net"]'
        )
        logoLinks.forEach((link) => {
          ;(link as HTMLElement).style.display = 'none'
        })

        // 네이버 로고 이미지 숨기기
        const logoImages = mapContainer.querySelectorAll(
          'img[alt="NAVER"], img[src*="naver-logo"]'
        )
        logoImages.forEach((img) => {
          ;(img as HTMLElement).style.display = 'none'
        })

        // 저작권 표시 숨기기 (좌측 하단)
        const copyrightElements = mapContainer.querySelectorAll(
          '.map_copyright, div[class*="copyright"]'
        )
        copyrightElements.forEach((el) => {
          ;(el as HTMLElement).style.display = 'none'
        })

        // "© NAVER Corp." 텍스트를 포함한 요소 숨기기
        const allElements = mapContainer.querySelectorAll('*')
        allElements.forEach((el) => {
          const text = el.textContent || ''
          if (text.includes('© NAVER Corp.') || text.includes('NAVER Corp')) {
            ;(el as HTMLElement).style.display = 'none'
          }
        })

        // 하단 컨트롤 영역 숨기기 (우측: 스케일 및 로고, 좌측: 저작권)
        const controlDivs = mapContainer.querySelectorAll(
          'div[style*="position: absolute"]'
        )
        controlDivs.forEach((div) => {
          const element = div as HTMLElement
          const style = element.getAttribute('style') || ''
          if (
            style.includes('bottom: 0px') &&
            (style.includes('right: 0px') || style.includes('left: 0px')) &&
            (style.includes('z-index: 100') || style.includes('z-index:100'))
          ) {
            element.style.display = 'none'
          }
        })

        // 도로명 주소 및 지하철역 제외한 세세한 정보 숨기기
        // 건물명, 상호명 등 텍스트 레이어 숨기기
        const textLayers = mapContainer.querySelectorAll(
          'div[class*="label"], div[class*="text"], div[class*="name"]'
        )
        textLayers.forEach((el) => {
          const element = el as HTMLElement
          const text = element.textContent || ''
          const className = element.className || ''

          // 마커 라벨은 제외 (우리가 만든 마커 라벨)
          if (className.includes('marker-label')) {
            return
          }

          // 도로명 주소와 지하철역은 제외
          const isRoadAddress =
            text.match(/로$|길$|대로$/) || className.includes('road')
          const isSubwayStation =
            text.includes('역') &&
            (text.includes('선') ||
              className.includes('subway') ||
              className.includes('station'))

          // 도로명 주소와 지하철역이 아닌 경우에만 숨기기
          if (!isRoadAddress && !isSubwayStation) {
            // 건물명, 상호명 등 세세한 정보 숨기기
            if (
              className.includes('building') ||
              className.includes('poi') ||
              className.includes('place')
            ) {
              element.style.display = 'none'
            }
          }
        })

        // SVG 텍스트 요소 숨기기 (건물명, 상호명 등)
        const svgTexts = mapContainer.querySelectorAll('text')
        svgTexts.forEach((el) => {
          const element = el as SVGTextElement
          const text = element.textContent || ''
          const parent = element.parentElement

          // 도로명 주소와 지하철역은 제외
          const isRoadAddress = text.match(/로$|길$|대로$/)
          const isSubwayStation =
            text.includes('역') &&
            (text.includes('선') ||
              parent?.className.includes('subway') ||
              parent?.className.includes('station'))

          // 도로명 주소와 지하철역이 아닌 경우에만 숨기기
          if (!isRoadAddress && !isSubwayStation) {
            // 건물명, 상호명 등 세세한 정보 숨기기
            if (
              parent?.className.includes('building') ||
              parent?.className.includes('poi') ||
              parent?.className.includes('place')
            ) {
              element.style.display = 'none'
            }
          }
        })
      }

      // 맵 렌더링 후 컨트롤 숨기기 (약간의 지연 후 실행)
      setTimeout(() => {
        hideNaverMapControls()
      }, 100)

      // 맵 변경 시 컨트롤만 숨기기 및 버튼 표시 여부 확인
      const handleMapChange = () => {
        // 컨트롤이 다시 나타날 수 있으므로 계속 숨기기
        hideNaverMapControls()

        // 지도 중심 위치 업데이트
        const center = mapInstance.getCenter()
        if (center) {
          setMapCenter({
            lat: center.lat(),
            lng: center.lng(),
          })
        }

        // 마지막으로 불러온 bounds가 없으면 버튼 표시하지 않음
        if (!lastLoadedBoundsRef.current) {
          return
        }

        // 현재 bounds 계산
        const bounds = mapInstance.getBounds()
        if (!bounds) {
          return
        }

        const sw = bounds.getSW ? bounds.getSW() : bounds.getSouthWest()
        const ne = bounds.getNE ? bounds.getNE() : bounds.getNorthEast()

        if (!sw || !ne) {
          return
        }

        const currentBounds = {
          north: ne.lat(),
          south: sw.lat(),
          east: ne.lng(),
          west: sw.lng(),
        }

        const lastBounds = lastLoadedBoundsRef.current

        // 마지막으로 불러온 bounds에서 조금이라도 달라지면(이동·줌인·줌아웃 모두) 버튼 표시.
        // 동일 위치에서의 idle/settle(미세 float 노이즈)은 EPS로 걸러 불필요한 표시를 막는다.
        const EPS = 1e-9
        const boundsChanged =
          Math.abs(currentBounds.north - lastBounds.north) > EPS ||
          Math.abs(currentBounds.south - lastBounds.south) > EPS ||
          Math.abs(currentBounds.east - lastBounds.east) > EPS ||
          Math.abs(currentBounds.west - lastBounds.west) > EPS

        setShouldShowReloadButton(boundsChanged)
      }

      // 지도 이벤트 리스너 등록 (컨트롤 숨기기만)
      // center_changed: 중심점이 변경될 때 (지도 이동 시)
      naverMaps.Event.addListener(
        mapInstance,
        'center_changed',
        handleMapChange
      )

      // bounds_changed: 지도 영역이 변경될 때 (이동, 줌 모두 포함)
      naverMaps.Event.addListener(
        mapInstance,
        'bounds_changed',
        handleMapChange
      )

      // idle: 지도 이동/줌 완료 시 (추가 보장)
      naverMaps.Event.addListener(mapInstance, 'idle', handleMapChange)

      // zoom_changed: 줌 레벨 변경 시
      naverMaps.Event.addListener(mapInstance, 'zoom_changed', () => {
        const currentZoom = mapInstance.getZoom()
        console.log('[MainMap] zoom_changed', currentZoom)
        setZoom(currentZoom)
        handleMapChange()
      })

      // click: 지도 어디든 클릭 시 — 그 좌표로 카카오 가게 검색
      // 카카오 결과를 우리 DB에 등록(또는 기존 가게 가져오기) 후 바텀시트 열기.
      // 단, 사용자 클릭에서 TAP_RADIUS 이내 + 음식점/카페 카테고리만 채택.
      naverMaps.Event.addListener(mapInstance, 'click', async (e: any) => {
        const lat = e.coord?.y ?? e.latlng?.y ?? e.latlng?.lat?.()
        const lng = e.coord?.x ?? e.latlng?.x ?? e.latlng?.lng?.()
        if (typeof lat !== 'number' || typeof lng !== 'number') {
          console.warn('[MainMap] click: cannot extract coords', e)
          return
        }

        // 탭 지점 주변의 유효 장소를 '전부' 모은다.
        // 예전에는 가장 가까운 한 곳(places[0])만 봤는데, 한 건물에 여러 가게가
        // 있으면 좌표가 같거나 몇 m 차이라 사실상 아무거나 집혔다.
        // (2층 카페와 3층 식당은 위경도가 완전히 동일하다)
        const places = await kakaoApi.searchByCoordinate(lat, lng, 30)
        const candidates = places.filter((place) => {
          const distance = parseInt(place.distance ?? '9999', 10)
          const category = place.category_name ?? ''
          return (
            distance <= TAP_RADIUS &&
            (category.includes('음식점') || category.includes('카페'))
          )
        })

        if (candidates.length === 0) {
          // 빈 곳 탭 → 선택 해제(위치핀 제거 + 접시·점 복귀). 네이버 동일.
          if (clickMarkerRef.current) {
            clickMarkerRef.current.setMap(null)
            clickMarkerRef.current = null
          }
          setFocusedPlacePinMarker(null)
          setPreviewPinMarker(null)
          setHasLocationMarker(false)
          return
        }

        if (candidates.length === 1) {
          void selectTappedPlaceRef.current?.(candidates[0])
          return
        }

        // 좌표로는 가릴 수 없으니 사용자에게 묻는다
        setTapCandidates(candidates)
      })
    } catch (error: any) {
      console.error('[MainMap] 네이버 맵 초기화 실패:', {
        message: error?.message,
        stack: error?.stack,
        naver: typeof (window as any).naver,
        maps: typeof (window as any).naver?.maps,
      })
      const message = error?.message || ''
      // Script error: 외부 스크립트(네이버 맵) 인증 실패 시 발생. 웹 서비스 URL 등록 필요
      const isScriptError =
        message === 'Script error.' ||
        (typeof message === 'string' && message.includes('Script error'))
      setMapLoadError(
        isScriptError
          ? '지도 로드에 실패했습니다. 네이버 클라우드 플랫폼에서 현재 URL(웹 서비스 URL)을 등록해주세요.'
          : message || '지도를 불러오는데 실패했습니다'
      )
      setIsMapLoaded(false)
    }
  }

  // 두 좌표 간 거리 계산 (미터 단위)
  const calculateDistance = useCallback(
    (lat1: number, lng1: number, lat2: number, lng2: number): number => {
      const R = 6371000 // 지구 반지름 (미터)
      const dLat = ((lat2 - lat1) * Math.PI) / 180
      const dLng = ((lng2 - lng1) * Math.PI) / 180
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((lat1 * Math.PI) / 180) *
          Math.cos((lat2 * Math.PI) / 180) *
          Math.sin(dLng / 2) *
          Math.sin(dLng / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      return R * c
    },
    []
  )


  // 플레이트리스트 필터 시트가 스피너 없이 바로 뜨도록 지도 진입 시 미리 로드.
  // 고를 리스트가 하나도 없으면 '플레이트리스트' 칩을 아예 안 보인다
  // (눌러도 '플레이트리스트가 없습니다'만 뜨는 칩이었다).
  //
  // 못 받아오면 칩은 숨긴 채로 두고 조금씩 늦춰 다시 묻는다. 있으면 그때 붙는다 —
  // 없는 사람에게 눌러도 비어 있는 칩을 보여주는 것보다 낫다.
  useEffect(() => {
    let alive = true
    let timer: ReturnType<typeof setTimeout> | null = null
    const RETRY_DELAYS_MS = [2000, 5000, 15000]

    const check = (attempt: number) => {
      prefetchPlaylistFilter().then((count) => {
        if (!alive) return
        if (count !== null) {
          setHasPlaylists(count > 0)
          return
        }
        if (attempt < RETRY_DELAYS_MS.length) {
          timer = setTimeout(() => check(attempt + 1), RETRY_DELAYS_MS[attempt])
        }
      })
    }
    check(0)

    return () => {
      alive = false
      if (timer) clearTimeout(timer)
    }
  }, [])

  // 초기화 useEffect (페이지 진입 시 한 번만 실행)
  useEffect(() => {
    // 메인 페이지 진입 시 이전 히스토리 정리
    clearAuthHistory()

    // 쿼리 파라미터에서 placeId 확인하여 바텀 시트 열기 및 지도 중심 이동용 좌표 조회
    const placeIdFromQuery = searchParams.get('placeId')
    const placeLatStr = searchParams.get('placeLat')
    const placeLngStr = searchParams.get('placeLng')
    const placeNameFromQuery = searchParams.get('placeName')
    const placeAddressFromQuery = searchParams.get('placeAddress')
    const moveLatStr = searchParams.get('moveLat')
    const moveLngStr = searchParams.get('moveLng')

    if (placeIdFromQuery) {
      setPreviewPinMarker(null)
      setFocusedPlacePinMarker(null)
      setIsPreviewBottomSheetOpen(false)
      hasNavigatedFromPlaceIdRef.current = true
      // 장소로 카메라 이동이 끝날 때까지 오버레이 유지(기본중심 깜빡임 방지)
      setIsFocusPending(true)
      setTimeout(() => setIsFocusPending(false), 5000) // 안전장치(응답 지연/실패 시 무한대기 방지)
      setSelectedStoreId(placeIdFromQuery)
      setIsBottomSheetOpen(true)
      mapApi
        .getPlace(placeIdFromQuery)
        .then((place) =>
          setPlaceToFocus({
            id: String(place.id),
            position: { lat: place.lat, lng: place.lng },
            type: place.category,
            label: place.name,
            isBookmarked: place.is_bookmarked,
          })
        )
        .catch(() => setIsFocusPending(false))
    } else if (placeLatStr && placeLngStr) {
      const lat = parseFloat(placeLatStr)
      const lng = parseFloat(placeLngStr)
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        setFocusedPlacePinMarker(null)
        hasNavigatedFromPlaceIdRef.current = true
        // 좌표 진입도 동일하게 이동 완료까지 오버레이 유지
        setIsFocusPending(true)
        setTimeout(() => setIsFocusPending(false), 5000)
        // placeId 진입과 달리 좌표만 넘어온 경우 필터는 유지 (기본 저장한 장소 등)
        const label =
          placeNameFromQuery && placeNameFromQuery.trim() !== ''
            ? placeNameFromQuery
            : '장소'
        setPlaceToFocus({
          id: `preview-${lat}-${lng}`,
          position: { lat, lng },
          type: 'restaurant',
          label,
          address: placeAddressFromQuery ?? '',
          isBookmarked: false,
        })
      }
    } else if (moveLatStr && moveLngStr) {
      // 지하철역 검색 등 — 마커/핀 없이 지도 카메라만 그 좌표로 이동
      const lat = parseFloat(moveLatStr)
      const lng = parseFloat(moveLngStr)
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        hasNavigatedFromPlaceIdRef.current = true
        setCenterToMove({ lat, lng })
      }
    }

    // 카테고리 정보 가져오기
    loadCategories()

    // 지도 클러스터 설정 가져오기
    mapApi
      .getSettings()
      .then((settings) => {
        const zoomValue = Number(settings.cluster_dissolve_zoom)
        if (Number.isFinite(zoomValue)) {
          const normalizedZoom = Math.min(
            MAX_ZOOM,
            Math.max(HIGH_DENSITY_CLUSTER_MAX_ZOOM + 1, Math.round(zoomValue))
          )
          setClusterDissolveZoom(normalizedZoom)
        }
      })
      .catch(() => {})

    // 지도를 먼저 기본 위치로 초기화 (위치 정보를 기다리지 않음)
    const checkAndInitMap = () => {
      if (!mapContainerRef.current) {
        setTimeout(checkAndInitMap, 100)
        return
      }
      initMap()
        .catch((err) => {
          console.error('[MainMap] initMap 실패:', err)
        })
        .finally(() => {
          setIsMapInitialized(true)
        })
    }
    checkAndInitMap()

    // 위치 정보 요청 (지도 초기화와 병렬로 진행)
    if (isApp() && (window as any).FlutterBridge) {
      // 앱 환경에서는 위치 권한을 먼저 요청
      requestLocationPermission().then((granted) => {
        if (granted) {
          getUserLocation()
        }
      })
    } else {
      // 웹 환경에서는 바로 위치 정보 요청
      getUserLocation()
    }

    return () => {
      if (map) {
        map.destroy()
        setMap(null)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 지도 초기화 시 중심 위치 설정
  useEffect(() => {
    if (map) {
      const center = map.getCenter()
      if (center) {
        setMapCenter({
          lat: center.lat(),
          lng: center.lng(),
        })
      }
    }
  }, [map])

  // 마커 데이터 가져오기
  const fetchMarkers = useCallback(
    async (filter: MapFilterType | null, force = false) => {
      if (!map) return

      // 필터가 선택되지 않았으면 마커를 모두 비우고 종료
      if (!filter) {
        setMarkers([])
        lastLoadedBoundsRef.current = null
        setShouldShowReloadButton(false)
        return
      }

      // 로딩 중이면 무시 (placeId로 이동 시 강제 로드는 예외)
      if (!force && isLoadingMarkersRef.current) {
        return
      }

      setIsLoadingMarkers(true)
      isLoadingMarkersRef.current = true
      try {
        // bounds 계산
        const bounds = map.getBounds()
        if (!bounds) {
          setIsLoadingMarkers(false)
          isLoadingMarkersRef.current = false
          return
        }

        // 네이버 맵 bounds 확인 (isEmpty 메서드가 없을 수 있으므로 직접 확인)
        const sw = bounds.getSW ? bounds.getSW() : bounds.getSouthWest()
        const ne = bounds.getNE ? bounds.getNE() : bounds.getNorthEast()

        if (!sw || !ne) {
          setIsLoadingMarkers(false)
          isLoadingMarkersRef.current = false
          return
        }

        const boundsData = {
          north: ne.lat(),
          south: sw.lat(),
          east: ne.lng(),
          west: sw.lng(),
        }

        const response = await mapApi.getMarkers({
          bounds: boundsData,
          filter: filter,
          bookmarkCategoryId:
            filter === MapFilterType.PLAYLIST
              ? selectedPlaylistIdRef.current
              : undefined,
        })

        setMarkers(response.markers)
        lastLoadedBoundsRef.current = boundsData
        setShouldShowReloadButton(false)
      } catch (error: any) {
        console.error('마커 데이터 가져오기 실패:', error)
        // 에러 응답의 상세 정보 출력
        if (error?.response?.data) {
          console.error('에러 상세 정보:', {
            message: error.response.data.message,
            error: error.response.data.error,
            errors: error.response.data.errors,
          })
        }
        setMarkers([])
      } finally {
        setIsLoadingMarkers(false)
        isLoadingMarkersRef.current = false
      }
    },
    [map]
  )

  // placeId·좌표 쿼리 진입 시 해당 좌표로 지도 중심 이동 (좌표만 넘어온 건 previewPinMarker, DB 장소는 focusedPlacePinMarker로 fetch와 분리)
  useEffect(() => {
    if (!map || !placeToFocus) return

    const naverMaps = (window as any).naver?.maps
    if (!naverMaps) return

    const { position } = placeToFocus
    const newCenter = new naverMaps.LatLng(position.lat, position.lng)
    map.setCenter(newCenter)
    map.setZoom(FOCUS_ZOOM) // 검색 장소는 조금 더 확대
    setMapCenter(position)

    // 검색/포커스 장소에 클릭 때와 동일한 핀 마커 표시 (바텀시트 닫으면 제거)
    if (clickMarkerRef.current) {
      clickMarkerRef.current.setMap(null)
    }
    clickMarkerRef.current = new naverMaps.Marker({
      position: newCenter,
      map,
      icon: {
        url: '/images/clicked-location-marker.png',
        size: new naverMaps.Size(40, 40),
        scaledSize: new naverMaps.Size(40, 40),
        anchor: new naverMaps.Point(20, 40),
      },
      zIndex: 300,
    })
    setHasLocationMarker(true) // 위치핀 활성 → 접시·점 숨김

    const isPreviewOnly = placeToFocus.id.startsWith('preview-')
    if (isPreviewOnly) {
      setFocusedPlacePinMarker(null)
      setPreviewPinMarker(placeToFocus)
      setIsPreviewBottomSheetOpen(true)
      setIsBottomSheetOpen(false)
    } else {
      setPreviewPinMarker(null)
      setIsPreviewBottomSheetOpen(false)
      setFocusedPlacePinMarker(placeToFocus)
    }

    lastLoadedBoundsRef.current = {
      north: position.lat + 0.01,
      south: position.lat - 0.01,
      east: position.lng + 0.01,
      west: position.lng - 0.01,
    }
    setPlaceToFocus(null)
    // 카메라가 대상 장소로 이동 완료 → 오버레이 해제(이제 노출해도 서울시청이 안 보임)
    setIsFocusPending(false)
  }, [map, placeToFocus])

  // moveLat/moveLng 쿼리로 진입 (지하철역 검색 등) — 마커 없이 카메라만 이동
  useEffect(() => {
    if (!map || !centerToMove) return

    const naverMaps = (window as any).naver?.maps
    if (!naverMaps) return

    const newCenter = new naverMaps.LatLng(centerToMove.lat, centerToMove.lng)
    map.setCenter(newCenter)
    map.setZoom(defaultZoom)
    setMapCenter(centerToMove)

    lastLoadedBoundsRef.current = {
      north: centerToMove.lat + 0.01,
      south: centerToMove.lat - 0.01,
      east: centerToMove.lng + 0.01,
      west: centerToMove.lng - 0.01,
    }
    setCenterToMove(null)
  }, [map, centerToMove])

  // 위치핀(검색/클릭) 제거 → 접시·점 복귀
  const clearLocationMarker = useCallback(() => {
    if (clickMarkerRef.current) {
      clickMarkerRef.current.setMap(null)
      clickMarkerRef.current = null
    }
    setFocusedPlacePinMarker(null)
    setPreviewPinMarker(null)
    setHasLocationMarker(false)
  }, [])

  // 지도 중심을 사용자 위치로 이동
  const moveToUserLocation = useCallback(() => {
    if (!map || !userLocation) return

    const naverMaps = (window as any).naver.maps
    const newCenter = new naverMaps.LatLng(userLocation.lat, userLocation.lng)
    map.setCenter(newCenter)
    map.setZoom(defaultZoom)
    // 지도 중심 업데이트
    setMapCenter({
      lat: userLocation.lat,
      lng: userLocation.lng,
    })

    // 내 위치로 날아가므로 머물던 장소의 위치핀은 뗀다.
    // 안 떼면 hasLocationMarker 가 켜진 채라 filteredMarkers 가 빈 배열을 돌려주고,
    // 마커를 새로 받아와도 접시·점이 한 개도 안 그려진다('이 위치에서 검색'과 같은 원인).
    clearLocationMarker()

    // 지도 이동 후 약간의 지연을 두고 마커 다시 불러오기
    setTimeout(() => {
      if (map && !isLoadingMarkersRef.current) {
        fetchMarkers(selectedFilterRef.current)
      }
    }, 100)
  }, [map, userLocation, fetchMarkers, clearLocationMarker])

  // 기본 선택을 '경험한 장소'로 물렸으면, 이미 받은 '기록된 장소' 마커를 바꿔 끼운다
  useEffect(() => {
    if (!recordedFallbackRef.current || !map) return
    if (selectedFilter !== MapFilterType.EXPERIENCED) return
    recordedFallbackRef.current = false
    fetchMarkers(MapFilterType.EXPERIENCED, true)
  }, [map, selectedFilter, fetchMarkers])

  // GPS 버튼 클릭: 위치가 있으면 내 위치로 이동, 없으면(권한 거부/미획득) 권한 재요청 후 이동
  const handleMyLocationClick = useCallback(async () => {
    if (userLocation) {
      moveToUserLocation()
      return
    }

    const granted = await requestLocationPermission()
    if (!granted) {
      // iOS는 한 번 거부하면 팝업을 다시 안 띄운다. 켜는 길을 알려준다
      await guideLocationPermission()
      return
    }

    await getUserLocation()
    const loc = useLocationStore.getState().location
    if (!loc) {
      toastUtil.error('위치를 가져올 수 없습니다')
      return
    }

    const naverMaps = (window as any).naver?.maps
    if (map && naverMaps) {
      map.setCenter(new naverMaps.LatLng(loc.lat, loc.lng))
      map.setZoom(defaultZoom)
      setMapCenter({ lat: loc.lat, lng: loc.lng })
      clearLocationMarker()
      setTimeout(() => {
        if (map && !isLoadingMarkersRef.current) {
          fetchMarkers(selectedFilterRef.current)
        }
      }, 100)
    }
  }, [
    userLocation,
    moveToUserLocation,
    requestLocationPermission,
    getUserLocation,
    map,
    fetchMarkers,
    clearLocationMarker,
  ])

  // 본인 위치 마커 생성/업데이트
  useEffect(() => {
    if (!map || !userLocation) {
      // 위치 정보가 없으면 마커 제거
      if (userLocationMarkerRef.current) {
        userLocationMarkerRef.current.setMap(null)
        userLocationMarkerRef.current = null
      }
      return
    }

    const naverMaps = (window as any).naver.maps

    // 마커가 없으면 새로 생성
    if (!userLocationMarkerRef.current) {
      // 작은 원형 마커 컨테이너 생성
      const markerContainer = document.createElement('div')
      markerContainer.className = 'user-location-marker'
      markerContainer.style.width = '32px'
      markerContainer.style.height = '32px'
      markerContainer.style.position = 'relative'
      markerContainer.style.display = 'flex'
      markerContainer.style.alignItems = 'center'
      markerContainer.style.justifyContent = 'center'

      // 펄스 애니메이션을 위한 외부 원 생성
      const pulseRing = document.createElement('div')
      pulseRing.className = 'user-location-pulse'
      pulseRing.style.position = 'absolute'
      pulseRing.style.top = '50%'
      pulseRing.style.left = '50%'
      pulseRing.style.transform = 'translate(-50%, -50%)'
      pulseRing.style.width = '16px'
      pulseRing.style.height = '16px'
      pulseRing.style.borderRadius = '50%'
      pulseRing.style.border = '2px solid #4285F4'
      pulseRing.style.opacity = '0.6'
      pulseRing.style.animation =
        'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
      pulseRing.style.pointerEvents = 'none'

      // 내부 원형 마커 생성
      const innerCircle = document.createElement('div')
      innerCircle.style.width = '16px'
      innerCircle.style.height = '16px'
      innerCircle.style.borderRadius = '50%'
      innerCircle.style.backgroundColor = '#4285F4'
      innerCircle.style.border = '3px solid white'
      innerCircle.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)'
      innerCircle.style.position = 'relative'
      innerCircle.style.zIndex = '1'

      markerContainer.appendChild(pulseRing)
      markerContainer.appendChild(innerCircle)

      // 새 마커 생성
      const marker = new naverMaps.Marker({
        position: new naverMaps.LatLng(userLocation.lat, userLocation.lng),
        map: map,
        icon: {
          content: markerContainer,
          anchor: new naverMaps.Point(16, 16), // 중심점
        },
        zIndex: 1000, // 다른 마커보다 위에 표시
      })
      userLocationMarkerRef.current = marker
    } else {
      // 기존 마커가 있으면 위치만 업데이트
      userLocationMarkerRef.current.setPosition(
        new naverMaps.LatLng(userLocation.lat, userLocation.lng)
      )
    }

    return () => {
      if (userLocationMarkerRef.current) {
        userLocationMarkerRef.current.setMap(null)
        userLocationMarkerRef.current = null
      }
    }
  }, [map, userLocation])

  // 지도가 로드되고 위치 정보가 있으면 본인 위치로 이동
  // (placeId로 진입한 경우는 해당 장소를 보여주므로 제외)
  useEffect(() => {
    if (map && userLocation && isMapInitialized) {
      // placeId로 검색 결과에서 진입한 경우 자동 이동하지 않음
      if (hasNavigatedFromPlaceIdRef.current) return

      // 지도 중심이 사용자 위치와 다를 때만 이동
      const center = map.getCenter()
      if (center) {
        const currentCenter = { lat: center.lat(), lng: center.lng() }
        const distance = calculateDistance(
          currentCenter.lat,
          currentCenter.lng,
          userLocation.lat,
          userLocation.lng
        )
        // 100m 이상 떨어져 있으면 이동
        if (distance > 100) {
          moveToUserLocation()
        }
      }
    }
  }, [
    map,
    userLocation,
    moveToUserLocation,
    isMapInitialized,
    calculateDistance,
  ])

  const handleSearchBarClick = () => {
    navigate('/search')
  }

  const handleFilterSelect = (filterId: string) => {
    const filterType = filterId as MapFilterType
    // 위치마커가 떠 있으면 제거하고 필터 마커(접시·점)를 표시
    clearLocationMarker()

    // 플레이트리스트: 버튼 탭 시 선택 시트 열기 (이미 적용 중이면 해제)
    if (filterType === MapFilterType.PLAYLIST) {
      if (selectedFilter === MapFilterType.PLAYLIST) {
        setSelectedFilter(null)
        setSelectedPlaylistId(null)
        if (map && !isLoadingMarkersRef.current) {
          fetchMarkers(null)
        }
      } else {
        setIsPlaylistSheetOpen(true)
      }
      return
    }

    // 선택한 필터를 다시 클릭하면 필터 해제 (마커 모두 숨김)
    const newFilter = selectedFilter === filterType ? null : filterType
    setSelectedFilter(newFilter)
    setSelectedPlaylistId(null)

    // 필터 변경 시 마커 다시 가져오기 (null이면 fetchMarkers 내부에서 비움)
    if (map && !isLoadingMarkersRef.current) {
      fetchMarkers(newFilter)
    }
  }

  // 플레이트리스트 선택 → 그 리스트 장소만 지도에 표시
  const handlePlaylistSelected = (playlistId: number) => {
    clearLocationMarker()
    setIsPlaylistSheetOpen(false)
    setSelectedFilter(MapFilterType.PLAYLIST)
    setSelectedPlaylistId(playlistId)
    // fetchMarkers가 ref를 읽으므로 호출 전에 즉시 반영
    selectedPlaylistIdRef.current = playlistId
    selectedFilterRef.current = MapFilterType.PLAYLIST
    if (map && !isLoadingMarkersRef.current) {
      fetchMarkers(MapFilterType.PLAYLIST)
    }
  }

  const handleMarkerClick = useCallback(
    (markerId: string) => {
      if (markerId.startsWith('preview-')) {
        setIsBottomSheetOpen(false)
        setSelectedStoreId(null)
        setIsPreviewBottomSheetOpen(true)
        return
      }
      setFocusedPlacePinMarker((prev) =>
        prev && prev.id !== markerId ? null : prev
      )
      setIsPreviewBottomSheetOpen(false)
      setSelectedStoreId(markerId)
      setIsBottomSheetOpen(true)
      // 쿼리 파라미터 업데이트
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.set('placeId', markerId)
      setSearchParams(newSearchParams, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  const handleClosePreviewBottomSheet = () => {
    setIsPreviewBottomSheetOpen(false)
    // 클릭/포커스 마커 제거
    if (clickMarkerRef.current) {
      clickMarkerRef.current.setMap(null)
      clickMarkerRef.current = null
    }
  }

  const handleCloseBottomSheet = () => {
    setIsBottomSheetOpen(false)
    setSelectedStoreId(null)
    // 마커는 그대로 유지 — 드래그/줌해도 남고, 지도의 다른 장소를 터치하면
    // 기존 map click 핸들러가 마커를 새 위치로 옮긴다.
    // 쿼리 파라미터 제거
    const newSearchParams = new URLSearchParams(searchParams)
    newSearchParams.delete('placeId')
    setSearchParams(newSearchParams, { replace: true })
  }

  // 선택된 마커의 이름 찾기
  const selectedStoreName = useMemo(() => {
    if (!selectedStoreId) return null
    const marker = markers.find((m) => m.id === selectedStoreId)
    if (marker?.label) return marker.label
    if (focusedPlacePinMarker?.id === selectedStoreId) {
      return focusedPlacePinMarker.label
    }
    return null
  }, [selectedStoreId, markers, focusedPlacePinMarker])

  // 렌더용 마커. 포커스/미리보기 장소는 카테고리 핀 대신 클릭 마커(clicked-location 이미지)로
  // 표시하므로, 그 장소의 카테고리 핀은 목록에서 제외해 중복을 막는다.
  const filteredMarkers: MapMarker[] = useMemo(() => {
    // 위치 핀(검색/클릭)이 떠 있으면 접시·점 전부 숨김 — 핀만 표시(네이버 동일)
    if (hasLocationMarker) return []
    return markers
  }, [markers, hasLocationMarker])

  // 클러스터링 제거: 줌 < DOT_ZOOM이면 점(dot), 그 이상이면 접시(dish)
  const isDotZoom = zoom < DOT_ZOOM

  useEffect(() => {
    console.log('[MainMap] zoom ↔ marker', {
      zoom,
      mode: zoom < DOT_ZOOM ? '점(dot)' : '접시(dish)',
    })
  }, [zoom])

  /**
   * 탭으로 고른 가게를 등록하고 바텀시트를 연다.
   *
   * 위치핀은 탭한 자리가 아니라 '고른 가게의 좌표'에 꽂는다.
   * 탭 지점은 최대 TAP_RADIUS만큼 어긋나 있어서, 그 자리에 핀을 두면
   * 가게가 실제보다 옆에 있는 것처럼 보인다.
   */
  const selectTappedPlace = useCallback(
    async (place: KakaoPlace) => {
      setTapCandidates([])

      const registered = await kakaoApi.registerKakaoPlace(place)
      if (!registered) {
        console.error('[MainMap] 카카오 가게 등록 실패')
        return
      }

      const naverMaps = (window as any).naver?.maps
      if (naverMaps && map) {
        const lat = parseFloat(place.y)
        const lng = parseFloat(place.x)

        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          if (clickMarkerRef.current) {
            clickMarkerRef.current.setMap(null)
          }
          clickMarkerRef.current = new naverMaps.Marker({
            position: new naverMaps.LatLng(lat, lng),
            map,
            icon: {
              url: '/images/clicked-location-marker.png',
              size: new naverMaps.Size(40, 40),
              scaledSize: new naverMaps.Size(40, 40),
              anchor: new naverMaps.Point(20, 40), // 핀 끝(바닥 중앙)이 가게 위치
            },
            zIndex: 300,
          })
          setHasLocationMarker(true) // 위치핀 활성 → 접시·점 숨김
        }
      }

      // 우리 DB의 place_id를 받아 기존 StoreBottomSheet 열기
      setSelectedStoreId(String(registered.place_id))
      setIsBottomSheetOpen(true)
    },
    [map]
  )

  useEffect(() => {
    selectTappedPlaceRef.current = selectTappedPlace
  }, [selectTappedPlace])

  const clearPlaceMarkers = useCallback(() => {
    if (markerClusteringRef.current) {
      markerClusteringRef.current.setMap(null)
      markerClusteringRef.current = null
    }

    markerClickListenersRef.current.forEach((listener) => {
      const naverMaps = (window as any).naver?.maps
      if (naverMaps) {
        naverMaps.Event.removeListener(listener)
      }
    })
    markerClickListenersRef.current = []

    placeMarkersRef.current.forEach((marker) => {
      marker.setMap(null)
    })
    placeMarkersRef.current = []
  }, [])

  // 줌아웃 시 표시하는 작은 점(dot). 접시 대신 장소마다 하나씩, 합치지 않음(Image #27).
  const createDotMarkerContent = useCallback(() => {
    const container = document.createElement('div')
    container.className = 'marker-container marker-clickable'
    const dot = document.createElement('div')
    dot.style.width = `${DOT_SIZE}px`
    dot.style.height = `${DOT_SIZE}px`
    dot.style.borderRadius = '50%'
    dot.style.background = '#8B7B6E' // 타우프(브랜드 브라운 계열)
    dot.style.border = '1.5px solid #ffffff'
    dot.style.boxShadow = '0 1px 3px rgba(0,0,0,0.25)'
    dot.style.boxSizing = 'border-box'
    container.appendChild(dot)
    return container
  }, [])

  // 경험한 장소 마커: 접시 프레임(가운데 투명) + 뒤에 깔린 원형 프로필 사진.
  // 프레임 안쪽 원 = 프레임 크기의 0.75 (PIL로 측정), 정중앙.
  const createProfileMarkerContent = useCallback(
    (profileUrl: string | null, size: number) => {
      const container = document.createElement('div')
      container.className = 'marker-container marker-clickable'

      const wrap = document.createElement('div')
      wrap.style.position = 'relative'
      wrap.style.width = `${size}px`
      wrap.style.height = `${size}px`

      const inner = Math.round(size * 0.75)
      const offset = (size - inner) / 2
      const photo = document.createElement('img')
      // 마커 얼굴은 30px 이라 원본(최대 1.7MB) 대신 128px 썸네일을 쓴다.
      // 썸네일이 아직 없으면 원본 → 그래도 안 되면 기본 이미지로 물러난다.
      photo.src = profileUrl ? thumbUrl(profileUrl) : '/images/profile-placeholder-icon.png'
      photo.style.position = 'absolute'
      photo.style.left = `${offset}px`
      photo.style.top = `${offset}px`
      photo.style.width = `${inner}px`
      photo.style.height = `${inner}px`
      photo.style.borderRadius = '50%'
      photo.style.objectFit = 'cover'
      // 기본 프로필(플레이스홀더)일 때만 80% 불투명도, 실제 사진은 그대로
      photo.style.opacity = profileUrl ? '1' : '0.8'
      photo.draggable = false
      photo.onerror = () => {
        if (profileUrl && photo.src !== profileUrl && photo.src.includes('/thumbs/')) {
          photo.src = profileUrl
          return
        }
        photo.onerror = null
        photo.src = '/images/profile-placeholder-icon.png'
        photo.style.opacity = '0.8'
      }

      const frame = document.createElement('img')
      frame.src = '/images/marker-plate-frame.png?v=3'
      frame.style.position = 'absolute'
      frame.style.left = '0'
      frame.style.top = '0'
      frame.style.width = `${size}px`
      frame.style.height = `${size}px`
      frame.draggable = false

      wrap.appendChild(photo)
      wrap.appendChild(frame)
      container.appendChild(wrap)
      return container
    },
    []
  )

  const createPlaceMarkerContent = useCallback(
    (
      marker: MapMarker,
      iconUrl: string,
      showLabel: boolean,
      iconWidth: number = MARKER_WIDTH,
      iconHeight: number = MARKER_HEIGHT,
      opacity: number = 1
    ) => {
      const container = document.createElement('div')
      container.className = 'marker-container marker-clickable'

      const markerWrapper = document.createElement('div')
      markerWrapper.className = 'marker-wrapper'
      markerWrapper.style.width = `${iconWidth}px`
      markerWrapper.style.height = `${iconHeight}px`

      const pinImg = document.createElement('img')
      pinImg.src = iconUrl
      pinImg.className = 'marker-pin'
      pinImg.style.width = `${iconWidth}px`
      pinImg.style.height = `${iconHeight}px`
      pinImg.style.opacity = `${opacity}`
      markerWrapper.appendChild(pinImg)

      container.appendChild(markerWrapper)

      // 마커에 장소 이름(라벨) 표시하지 않음
      void showLabel

      return container
    },
    []
  )

  useEffect(() => {
    const naverMaps = (window as any).naver?.maps

    if (!map || !naverMaps) {
      return
    }

    clearPlaceMarkers()

    if (filteredMarkers.length === 0) {
      return
    }

    const createdMarkers = filteredMarkers
      .map((marker) => {
        const isKnown =
          marker.hasReview ||
          marker.isPlaylistPlace ||
          marker.isBookmarked ||
          marker.isRecommended ||
          marker.isRecorded
        // 카테고리 없는 장소(ALL 필터, 현재 미사용)는 접시·점 둘 다 안 그림
        if (!isKnown) {
          return null
        }

        // 우선순위: 경험(프로필) > 저장(북마크) > 추천(별). 줌아웃이면 전부 점(dot).
        let markerContent: HTMLElement
        let anchor: any
        if (isDotZoom) {
          // 줌 < DOT_ZOOM → 종류 무관 작은 점, 합치지 않음(클러스터링 없음)
          markerContent = createDotMarkerContent()
          anchor = new naverMaps.Point(DOT_SIZE / 2, DOT_SIZE / 2)
        } else if (marker.isRecorded) {
          // 기록된 장소 → 같은 접시 프레임 + 가장 최근에 기록한 '남'의 사진.
          // 내가 기록한 곳이어도 남의 얼굴을 쓴다 — 내 마커는 경험한 장소에서 본다.
          // (그래서 hasReview 보다 먼저 본다)
          markerContent = createProfileMarkerContent(
            marker.recorderProfileImage ?? null,
            MARKER_BADGE_SIZE
          )
          anchor = new naverMaps.Point(MARKER_BADGE_SIZE / 2, MARKER_BADGE_SIZE / 2)
        } else if (marker.hasReview) {
          // 경험한 장소 → 접시 프레임 + 내 프로필 사진
          markerContent = createProfileMarkerContent(
            myProfileImage,
            MARKER_BADGE_SIZE
          )
          anchor = new naverMaps.Point(MARKER_BADGE_SIZE / 2, MARKER_BADGE_SIZE / 2)
        } else {
          // 플레이트리스트(내가 리뷰 안 함)·저장(북마크) → 저장 접시 마커, 추천 → 별.
          // 플레이트리스트=다 저장한 것이므로 저장 마커로 통일(추가자 프사 마커 제거).
          // 내가 경험(리뷰)한 곳은 위 hasReview 분기에서 이미 경험 마커로 처리됨.
          const iconUrl =
            marker.isBookmarked || marker.isPlaylistPlace
              ? '/images/marker-saved.png?v=6'
              : '/images/marker-visited.png?v=3'
          anchor = new naverMaps.Point(MARKER_BADGE_SIZE / 2, MARKER_BADGE_SIZE / 2)
          markerContent = createPlaceMarkerContent(
            marker,
            iconUrl,
            false,
            MARKER_BADGE_SIZE,
            MARKER_BADGE_SIZE,
            1
          )
        }

        const markerInstance = new naverMaps.Marker({
          position: new naverMaps.LatLng(marker.position.lat, marker.position.lng),
          // map을 여기서 붙이지 않음: MarkerClustering이 표시/숨김을 전담해야
          // 화면(bounds) 밖 마커가 클러스터에 안 들어간 채 개별 핀으로 남는 버그가 사라짐.
          // (클러스터링 끄는 줌에서는 아래에서 직접 setMap(map) 호출)
          icon: {
            content: markerContent,
            anchor,
          },
          title: marker.label,
          // 프로필 접시 마커(내 경험 or 플레이트리스트 소속)는 항상 최상위 레이어로 zIndex 상향
          // 기록된 장소 필터에선 내 기록이 남의 기록 위에 오도록 한다
          zIndex: marker.hasReview || marker.isPlaylistPlace ? 1000 : 100,
        })

        const markerClickListener = naverMaps.Event.addListener(
          markerInstance,
          'click',
          () => {
            handleMarkerClick(marker.id)
          }
        )

        markerClickListenersRef.current.push(markerClickListener)
        return markerInstance
      })
      .filter((marker): marker is naver.maps.Marker => marker !== null)

    placeMarkersRef.current = createdMarkers

    // 클러스터링 없음 — 각 마커를 직접 지도에 표시(합치지 않음). 줌에 따라 접시/점.
    createdMarkers.forEach((m) => m.setMap(map))

    return () => {
      clearPlaceMarkers()
    }
  }, [
    map,
    filteredMarkers,
    isDotZoom,
    handleMarkerClick,
    createPlaceMarkerContent,
    createProfileMarkerContent,
    createDotMarkerContent,
    myProfileImage,
    clearPlaceMarkers,
  ])

  const naverMapsClientId = process.env.REACT_APP_NAVER_MAPS_CLIENT_ID || ''

  if (!naverMapsClientId) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-backgroundColorBg1">
        <div className="text-center">
          <p className="text-style-captionL text-textColorText2 mb-8px">
            네이버 맵 API 키가 설정되지 않았습니다.
          </p>
          <p className="text-style-captionM text-textColorText3">
            REACT_APP_NAVER_MAPS_CLIENT_ID 환경 변수를 설정해주세요.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div className="app-container flex-1 flex flex-col">
        <div
          className={`w-full h-full relative overflow-hidden naver-map-wrapper ${isBottomSheetOpen || isPreviewBottomSheetOpen ? 'pointer-events-none' : ''}`}
        >
          {/* 지도 컨테이너 - 항상 렌더링하여 ref가 설정되도록 함 */}
          <div
            ref={mapContainerRef}
            style={mapContainerStyle}
            className="w-full h-full naver-map-container"
          ></div>

          {/* 로딩 오버레이 (지도 로드 전 + placeId/좌표 진입 시 장소로 카메라 이동 완료 전까지) */}
          {(!isMapLoaded || isFocusPending) && (
            <div className="absolute inset-0 flex items-center justify-center bg-backgroundColorBg1 bg-opacity-90 z-10">
              <div className="flex flex-col items-center justify-center">
                <LoadingSpinner size={28} borderWidth={4} />
                {mapLoadError && (
                  <p className="text-style-captionL text-textColorText2 mt-16px">
                    {mapLoadError}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* 로딩 중 맵 인터랙션 차단 오버레이 */}
          {isLoadingMarkers && isMapLoaded && (
            <div className="absolute inset-0 bg-transparent z-20" />
          )}

          {/* 마커 불러오는 동안 띄우던 하단 스피너는 뺐다 ('이 위치에서 검색'·GPS 버튼).
              응답이 0.2초 남짓이라 깜빡임만 남았다. 마커가 바뀌는 것으로 충분하다. */}

          {/* 다시 불러오기 버튼 (위치/줌 변경 시 표시, 불러오는 중이 아닐 때만) */}
          {isMapLoaded && shouldShowReloadButton && !isLoadingMarkers && (
            <div className="absolute bottom-20px left-1/2 transform -translate-x-1/2 z-30">
              <button
                onClick={() => {
                  /*
                   * 위치핀부터 떼고 다시 불러온다.
                   *
                   * 핀이 떠 있으면 filteredMarkers 가 빈 배열을 돌려줘서 접시·점이
                   * 하나도 안 그려진다(네이버와 같은 규칙). 자세히보기에서 돌아오면
                   * placeId 진입 처리가 그 장소에 핀을 꽂고 hasLocationMarker 를
                   * 켜두는데, 이 버튼만 그걸 안 껐다. 그래서 '경험한 장소'가 켜져
                   * 있는데도 눌러봐야 아무 변화가 없었다 — 마커는 제대로 받아오고
                   * 그리는 쪽에서 막혀 있었던 것이다.
                   *
                   * 필터 칩(handleFilterSelect)은 이미 같은 일을 한다. 이 버튼도
                   * '이 구역을 다시 보여달라'는 뜻이니 한 장소에 머무는 상태를 푸는 게 맞다.
                   */
                  if (map && !isLoadingMarkers) {
                    clearLocationMarker()
                    fetchMarkers(selectedFilter)
                  }
                }}
                className={`
                  px-32px
                  py-12px
                  bg-white
                  shadow-lg
                  font-pretendard
                  text-16px
                  hover:bg-gray-50
                  active:bg-gray-100
                  transition-colors
                  whitespace-nowrap
                  leading-none
                `}
                // 미선택 필터와 색상·폰트·corner radius(12) 통일
                style={{ color: '#8C8375', fontWeight: 500, borderRadius: 12 }}
              >
                이 위치에서 검색
              </button>
            </div>
          )}

          {/* GPS 버튼: 위치 있으면 내 위치로 이동, 없으면(권한 거부) 권한 재요청.
              화살표 이미지 자체가 버튼(흰 원 래퍼 없음) */}
          {isMapLoaded && (
            // '이 위치에서 검색' 버튼(높이 40px, bottom-20px)과 크기·정렬 통일:
            // 화살표 박스도 40px + bottom-20px → 높이·아래끝·중심선 모두 일치(시안과 동일 비율).
            <div className="absolute bottom-20px right-20px z-30">
              <button
                onClick={() => {
                  handleMyLocationClick()
                }}
                className="active:opacity-70 transition-opacity flex items-center"
              >
                <img
                  src="/images/my-location-arrow-icon.png"
                  alt="내 위치로 이동"
                  className="object-contain"
                  style={{ width: 40, height: 40 }}
                  draggable={false}
                />
              </button>
            </div>
          )}
        </div>

        <div
          className="fixed left-0 right-0 w-full flex flex-col"
          style={{ top: 'calc(6px + env(safe-area-inset-top))' }}
        >
          <div className="app-container px-20px">
            <SearchBar
              placeholder="검색"
              onClick={handleSearchBarClick}
              height={MAP_SEARCH_BAR_HEIGHT}
            />
          </div>
          <div className="h-10px"></div>
          <div className="app-container">
            {/* '추천'(POPULAR) 필터는 요청에 따라 임시 숨김 — 되돌리려면 .filter 제거 */}
            <CategoryFilter
              categories={
                MAP_FILTERS.filter(
                  (f) =>
                    f.id !== MapFilterType.POPULAR &&
                    (f.id !== MapFilterType.RECORDED || canUseRecordedFilter) &&
                    (f.id !== MapFilterType.PLAYLIST || hasPlaylists === true)
                ) as Category[]
              }
              selectedCategoryId={selectedFilter || undefined}
              onSelectCategory={handleFilterSelect}
            />
          </div>
        </div>
      </div>

      {/* 하단바 자리 스페이서 — 실제 BottomNav 높이와 동일해야 함.
          BottomNav은 iOS edge-to-edge에서 60px + env(safe-area-inset-bottom)로 커지므로
          스페이서도 같은 값으로 맞춘다. (안 맞추면 하단바가 지도영역을 침범해 플로팅 버튼과 겹침) */}
      <div
        className="w-full"
        style={{
          height: isIOS()
            ? `calc(${BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom))`
            : `${BOTTOM_NAV_HEIGHT}px`,
        }}
      ></div>

      <BottomNav activePath={RANKING_NAV_ITEM.path} />

      {/* 바텀 시트 */}
      <StoreBottomSheet
        storeId={selectedStoreId}
        storeName={selectedStoreName}
        isOpen={isBottomSheetOpen}
        onClose={handleCloseBottomSheet}
      />

      <PreviewPlaceBottomSheet
        isOpen={isPreviewBottomSheetOpen}
        onClose={handleClosePreviewBottomSheet}
        placeName={previewPinMarker?.label ?? ''}
        placeAddress={previewPinMarker?.address ?? ''}
      />

      {/* 한 자리에 가게가 여럿일 때 — 좌표로 못 고르니 직접 고르게 한다 */}
      <PlacePickBottomSheet
        isOpen={tapCandidates.length > 0}
        candidates={tapCandidates}
        onPick={selectTappedPlace}
        onClose={() => setTapCandidates([])}
      />

      {/* 플레이트리스트 필터 선택 시트 */}
      <PlaylistFilterSheet
        isOpen={isPlaylistSheetOpen}
        onClose={() => setIsPlaylistSheetOpen(false)}
        onSelect={handlePlaylistSelected}
      />
    </div>
  )
}

export default MainMap
